import React from "react";
import { Formik, Form } from "formik";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import * as Yup from "yup";
import CustomButton from "../common/button/CustomButton";
import theme from "../../theme";
import CustomInput from "../common/input/CustomInput";
import {
  StyledAddressFieldGrid,
  StyledButtonGroupWrapper,
  StyledProfileAddressFields,
  StyledTypography,
} from "./style";
import { useDispatch, useSelector } from "react-redux";
import { ADD_ADDRESS, GET_ADDRESS_LIST, UPDATE_ADDRESS } from "../../actions/AddressActions";
import toast from "react-hot-toast";
import { useAddressLocationData } from "../../hooks/useAddressLocationData";
import CountryStateFields from "../common/country-state-fields/CountryStateFields";
import { DEFAULT_ADDRESS_COUNTRY } from "../../utils/addressLocationOptions";

const EditAddress = ({ setAddAddress, addressId, addressList, embedded = false }) => {
  const currentAddressInfo = addressList.find((val) => val.id === addressId);
  const {
    countries,
    loadingCountries,
    getStatesForCountry,
    getCitiesForCountryState,
  } = useAddressLocationData();
  const dispatch = useDispatch();
  const submitLock = React.useRef(false);
  const isSubmitting = useSelector((state) => state?.address?.isLoading);

  const initialValues = {
    street_address: currentAddressInfo?.street_address || "",
    city: currentAddressInfo?.city || "",
    state: currentAddressInfo?.state || "",
    country: currentAddressInfo?.country || DEFAULT_ADDRESS_COUNTRY,
    zip_code: currentAddressInfo?.zip_code || "",
    address_type: currentAddressInfo?.address_type || "home",
  };

  const validationSchema = Yup.object({
    street_address: Yup.string().required("Street Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    zip_code: Yup.string().required("Zip code is required"),
  });

  const onSubmit = (values) => {
    if (submitLock.current) return;

    if (!addressId && addressList?.length >= 5) {
      toast.error("You are not allowed to add more than 5 address");
      return;
    }

    submitLock.current = true;

    const updatedValues = {
      ...(addressId && { id: addressId }),
      ...values,
      zip_code: String(values?.zip_code),
    };
    dispatch({
      type: addressId ? UPDATE_ADDRESS : ADD_ADDRESS,
      payload: {
        ...updatedValues,
        onSuccess: () => {
          setAddAddress(false);
          dispatch({ type: GET_ADDRESS_LIST });
        },
        onError: () => {
          submitLock.current = false;
        },
      },
    });
  };

  return (
    <Box
      p={embedded ? 0 : 3}
      borderRadius={embedded ? 0 : 2}
      bgcolor={embedded ? "transparent" : "#f1f8ff"}
      border={embedded ? "none" : "1px solid #cce0ff"}
      sx={embedded ? { minWidth: 0 } : undefined}
    >
      {!embedded && (
        <Typography variant="h6" mb={3}>
          Address Information
        </Typography>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({ errors, touched, handleChange, handleBlur, values, setFieldValue }) => (
          <Form>
            <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={embedded ? 2 : 3}
          >
            <Box display="flex" alignItems="center" gap="12px">
              <div style={{
                display: "flex",
                background: "#EAEAEA",
                borderRadius: "100px",
                padding: "3px",
                gap: "2px",
              }}>
                {["home", "work"].map((type) => (
                  <StyledTypography
                    key={type}
                    sx={{
                      cursor: "pointer",
                      background: values.address_type === type
                        ? theme.palette.custom.primary
                        : "transparent",
                      color: values.address_type === type
                        ? theme.palette.custom.white
                        : theme.palette.custom.lockerValueColor,
                      fontWeight: values.address_type === type
                        ? theme.typography.fontWeight.medium
                        : theme.typography.fontWeight.regular,
                      transition: "all 0.2s ease",
                      width: "80px",
                    }}
                    onClick={() => setFieldValue("address_type", type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </StyledTypography>
                ))}
              </div>
            </Box>
            </Box>
            {embedded ? (
              <Grid container spacing={2}>
                <StyledAddressFieldGrid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                  <CustomInput
                    required
                    formGrid
                    customLabel="Street Address"
                    name="street_address"
                    value={values?.street_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.street_address && Boolean(errors.street_address)
                    }
                    helperText={touched.street_address && errors.street_address}
                  />
                </StyledAddressFieldGrid>
                <CountryStateFields
                  fieldPrefix=""
                  index={0}
                  hideCountry={false}
                  countries={countries}
                  loadingCountries={loadingCountries}
                  getStatesForCountry={getStatesForCountry}
                  getCitiesForCountryState={getCitiesForCountryState}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  showZip
                  zipPath="zip_code"
                  formGrid
                  muiGridRow
                />
              </Grid>
            ) : (
              <StyledProfileAddressFields>
                <Grid container columnSpacing={2} rowSpacing={1.5}>
                  <StyledAddressFieldGrid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomInput
                      required
                      profileForm
                      customLabel="Street Address"
                      name="street_address"
                      value={values?.street_address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.street_address && Boolean(errors.street_address)
                      }
                      helperText={touched.street_address && errors.street_address}
                    />
                  </StyledAddressFieldGrid>
                  <CountryStateFields
                    fieldPrefix=""
                    index={0}
                    hideCountry={false}
                    countries={countries}
                    loadingCountries={loadingCountries}
                    getStatesForCountry={getStatesForCountry}
                    getCitiesForCountryState={getCitiesForCountryState}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    showZip
                    zipPath="zip_code"
                    muiGridRow
                  />
                </Grid>
              </StyledProfileAddressFields>
            )}

            <StyledButtonGroupWrapper
              style={embedded ? { marginTop: 16, justifyContent: "flex-end" } : undefined}
            >
              <CustomButton
                isprofile={!embedded}
                fontSize={theme.typography.base.fontSize}
                height={embedded ? "44px" : "54px"}
                btnLabel="Cancel"
                btntype={"white"}
                color={theme.palette.custom.lockerValueColor}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
                onClick={() => setAddAddress(false)}
              />

              <CustomButton
                isprofile={!embedded}
                fontSize={theme.typography.base.fontSize}
                height={embedded ? "44px" : "54px"}
                type="submit"
                btnLabel={!addressId ? "Add Address" : "Update Address"}
                btntype={"orderCards"}
                color={theme.palette.custom.white}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              />
            </StyledButtonGroupWrapper>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EditAddress;
